import React, { useEffect, useRef, useState } from "react";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { all_routes } from "../router/all_routes";
import { toast } from "react-toastify";
import { resendOTP, verifyOTP } from "../../services/auth.service";
import { useDispatch, useSelector } from "react-redux";
import {
  setHasSubscription,
  setLogin,
  setToken,
  setUserDetail,
} from "../../core/data/redux/user/userSlice";
import {
  removeStorageItem,
  setStorageItem,
} from "../../services/storage.service";
import http from "../../services/http.service";

const VerificationOtp = () => {
  const route = all_routes;
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [countdown, setCountdown] = useState(60); // Start countdown from 60 seconds

  const userDetail = useSelector((state: any) => state.user?.userDetail);
  const phone = userDetail?.phone || localStorage.getItem("phone");
  const email = userDetail?.email || localStorage.getItem("email") || localStorage.getItem("remember_email");
  const maskedPhone = phone ? phone.slice(-4) : "";

  // handle OTP input change
  const handleChange = (value: string, index: number) => {
    const newOtp = [...otp];
    newOtp[index] = value.replace(/\D/g, ""); // allow only digits
    setOtp(newOtp);

    // move focus to next if value entered
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  // handle backspace navigation
  const handleBackspace = (index: number, value: string) => {
    if (value === "" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join(""); // combine 6 digits
    const uuid = localStorage.getItem("uuid");

    if (!uuid) {
      toast.error("User Id not found. Please login again.");
      return;
    }

    if (otpValue.length !== 6) {
      toast.error("Please enter a 6-digit OTP");
      return;
    }

    try {
      setLoading(true);
      const result = await verifyOTP({ user_uuid: uuid, otp: otpValue });

      toast.success(result.data.message || "OTP Verified Successfully!");
      removeStorageItem("uuid");
      removeStorageItem("phone");

      setStorageItem("userData", JSON.stringify(result.data.data));
      setStorageItem("token", result.data?.token);

      dispatch(setLogin(true));
      dispatch(setUserDetail(result.data?.data));
      dispatch(setToken(result.data?.token));
      dispatch(setHasSubscription(result.data?.data?.has_subscription));

      http.defaults.headers["Authorization"] = `Token ${result.data?.token}`;

      // http.defaults.headers['Authorization'] = result.data?.token;
      navigate(route.successMessage, { state: { isLogin: true } }); // redirect after success
    } catch (error: any) {
      toast.error(error.response?.data?.error || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  // check if all OTP boxes are filled
  const isOtpComplete = otp.every((digit) => digit !== "");

  // Countdown effect
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null; // Initialize timer as null
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer); // Only clear if timer is defined
    };
  }, [countdown]);

  // Initialize countdown when component mounts
  useEffect(() => {
    setCountdown(60); // Start countdown on mount
  }, []);

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;
    const uuid = localStorage.getItem("uuid");

    if (!uuid) {
      toast.error("User Id not found. Please login again.");
      return;
    }

    try {
      setLoading(true);
      const result = await resendOTP({ user_uuid: uuid });
      toast.success(result.data.message || "OTP resent successfully!");
      setCountdown(60); // Reset countdown after resend
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  // Format countdown text
  const countdownText = countdown > 0 ? `after ${countdown} seconds` : "";

  return (
    <>
      {/* Main Wrapper */}
      <div className="main-wrapper authendication-pages">
        {/* Page Content */}
        <div className="content">
          <div className="container wrapper no-padding">
            <div className="row no-margin align-items-stretch">
              <div className="col-12 col-sm-12 col-lg-8 no-padding d-flex">
                <div className="banner-bg login">
                  <div className="row h100 pb-0">
                    <div className="col-sm-10 col-md-12 col-lg-12">
                      <div className="row h-100">
                        <div className="col-md-12 h-100">
                          <img
                            src="/assets/img/authImg.jpg"
                            className="object-fit-cover img-fluid w-100 h-100"
                            alt="authImg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-12 col-lg-4 no-padding d-flex">
                <div className="dull-pg shadow-card w-100">
                  <div className="row no-margin d-flex align-items-center justify-content-center">
                    <div className="col-sm-10 col-md-10 col-lg-11 mx-auto">
                      <header className="text-center">
                        <ImageWithBasePath
                          src="assets/img/logo.svg"
                          className="img-fluid"
                          alt="Logo"
                        />
                      </header>
                      <div className="otpWrapper">
                        <h2 className="text-center">
                          We sent a text to your phone
                        </h2>
                        <p className="text-center">
                          To continue, please enter the 6-digit verification code sent to your
                          <strong> registered phone number (ending in {maskedPhone})</strong> and
                          <strong> email address ({email})</strong>.
                        </p>
                        <p className="text-center">
                          Code are valid for 10 minutes.
                        </p>

                        <form onSubmit={handleSubmit}>
                          <div className="d-flex gap-2 otpWrap mb-3 justify-content-center mt-5">
                            {otp.map((digit, index) => (
                              <Form.Control
                                key={index}
                                type="text"
                                maxLength={1}
                                placeholder="-"
                                value={digit}
                                ref={(el) => (inputsRef.current[index] = el)}
                                onChange={(e) =>
                                  handleChange(e.target.value, index)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Backspace") {
                                    handleBackspace(
                                      index,
                                      e.currentTarget.value
                                    );
                                  }
                                }}
                                onPaste={(e) => {
                                  e.preventDefault();
                                  const paste = e.clipboardData
                                    .getData("text")
                                    .replace(/\D/g, ""); // only digits
                                  if (paste.length === 6) {
                                    setOtp(paste.split("")); // fill all boxes
                                    inputsRef.current[5]?.focus(); // move to last input
                                  }
                                }}
                                className="text-center"
                              />
                            ))}
                          </div>

                          <button
                            type="submit"
                            className="btn text-white primaryButton register-btn d-inline-flex justify-content-center align-items-center w-100 btn-block"
                            disabled={loading || !isOtpComplete}
                          >
                            Verify
                          </button>
                        </form>

                        <p className="text-center">
                          Didn’t receive the auth code?{" "}
                          <button
                            onClick={handleResendOTP}
                            disabled={countdown > 0 || loading}
                            className={countdown > 0 ? "disabled-link" : ""}
                          >
                            Re-Send
                          </button>{" "}
                          {countdownText && <span>{countdownText}</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Content */}
      </div>
      {/* /Main Wrapper */}
    </>
  );
};

export default VerificationOtp;
